/*
 * <modal-gate> — 무언가로 넘어가기 전에 확인을 받는 모달 게이트 (snippets/modal-gate.liquid).
 *
 * Wraps a <modal-box> and turns it into a checkpoint: something that would take the shopper
 * onward raises the modal instead, and it only happens once they have dealt with it. Two users
 * so far, and they need different halves of what is below:
 *
 *   - the 19+ age gate (snippets/adult-contents-modal.liquid) intercepts a link click and
 *     releases the same navigation after a confirmation;
 *   - the fan-sign members-only gate (snippets/fan-sign-event-form.liquid) is raised from the
 *     form itself and offers sign-in / sign-up links, so it has nothing to release.
 *
 * Nothing here knows about either — no products, no forms.
 *
 * The contract is three data attributes, all optional on their own:
 *
 *   [data-modal-gate="<modal id>"]  on any container whose links are gated. The value names
 *                                   the modal, so several gates can coexist on one page and
 *                                   each only claims its own. Leave it off entirely for a gate
 *                                   that is raised by code instead of by a click.
 *   [data-gate-continue]            the button that proceeds. It starts disabled and this
 *                                   element decides when to release it. A gate whose actions
 *                                   are plain links (sign in, sign up) has no such button.
 *   [data-gate-confirm]             control inside the modal — while it is present and
 *                                   unchecked, the continue button stays disabled. Style it
 *                                   however the design calls for; only the attribute is
 *                                   load-bearing.
 *
 * Continue dispatches a cancelable `modal-gate:confirm` on the <modal-gate> element carrying
 * the pending URL, and navigates there unless a listener preventDefaults — which is the hook
 * for a gate that has to do something other than follow a link.
 *
 * `open(url, opener)` is public, which is how a caller with a trigger that is not a link
 * (fan-sign's "Yes" radio and its submit button) raises the same gate. Pass no url when there
 * is nothing to navigate to afterwards.
 *
 * One instance per modal, listening on the document rather than on the gated containers:
 * cards and rows arrive after load in several places (predictive search, a collection grid
 * re-rendered by sort, the home grid swapped in by fetch), and a delegated listener is the
 * only one that keeps working for them.
 *
 * Note this fails open: with JS dead the modal cannot be shown at all, so gated links simply
 * navigate. The gate is a speed bump the design asks for, not an access control.
 */
if (!customElements.get('modal-gate')) {
  customElements.define(
    'modal-gate',
    class ModalGate extends HTMLElement {
      connectedCallback() {
        this.modalId = this.dataset.modal;
        this.modal = this.modalId && document.getElementById(this.modalId);
        if (!this.modal) return;

        this.dialog = this.modal.querySelector('dialog');
        // Both optional: a gate can have no button to release (its actions are links), and one
        // that does may have nothing to tick before releasing it.
        this.continueButton = this.querySelector('[data-gate-continue]');
        this.confirm = this.querySelector('[data-gate-confirm]');
        this.pendingUrl = null;

        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onConfirmChange = this.onConfirmChange.bind(this);
        this.onContinue = this.onContinue.bind(this);
        this.reset = this.reset.bind(this);

        // Both events: a plain click covers left click, Enter on a focused link and
        // cmd/ctrl-click (which would otherwise open the target in a new tab past the gate);
        // auxclick covers the middle click, which never fires 'click' at all.
        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('auxclick', this.onDocumentClick);
        this.confirm?.addEventListener('change', this.onConfirmChange);
        this.continueButton?.addEventListener('click', this.onContinue);
        // Fires for every dismissal — cancel, Escape, the backdrop — so the next thing to
        // raise the modal always starts from an unconfirmed state.
        this.dialog?.addEventListener('close', this.reset);

        this.reset();
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('auxclick', this.onDocumentClick);
        this.confirm?.removeEventListener('change', this.onConfirmChange);
        this.continueButton?.removeEventListener('click', this.onContinue);
        this.dialog?.removeEventListener('close', this.reset);
      }

      /* ---- Interception ------------------------------------------------------ */

      onDocumentClick(event) {
        // Something ahead of us already claimed this click (a quick-add opener, say).
        if (event.defaultPrevented) return;

        const link = event.target.closest?.('a[href]');
        if (!link) return;

        // Compared rather than built into a selector, so a modal id can hold any character
        // without needing to be escaped into one.
        const region = link.closest('[data-modal-gate]');
        if (!region || region.dataset.modalGate !== this.modalId) return;

        event.preventDefault();
        this.open(link.href, link);
      }

      /* ---- Modal ------------------------------------------------------------- */

      // Public: `url` is where continue should go afterwards, and may be omitted by a caller
      // that only wants the modal raised.
      open(url, opener) {
        this.pendingUrl = url || null;
        this.reset();

        // <modal-box> may not have upgraded if script-modal-box.js failed to load, so fall
        // back to the native <dialog> underneath it, which needs no JS of ours to open.
        if (typeof this.modal.show === 'function') {
          this.modal.show(opener);
        } else {
          this.dialog?.showModal();
        }
      }

      reset() {
        if (this.confirm) this.confirm.checked = false;
        if (this.continueButton) this.continueButton.disabled = Boolean(this.confirm);
      }

      onConfirmChange() {
        if (this.continueButton) this.continueButton.disabled = !this.confirm.checked;
      }

      onContinue() {
        if (this.confirm && !this.confirm.checked) return;
        if (!this.pendingUrl) return;

        const confirmed = this.dispatchEvent(
          new CustomEvent('modal-gate:confirm', {
            bubbles: true,
            cancelable: true,
            detail: { url: this.pendingUrl },
          })
        );
        if (!confirmed) return;
        // 모달이 닫히고 나서 실제 상품 페이지가 뜰 때까지의 빈 구간을 덮는다
        // (assets/script-page-loading.js). 이 이동은 클릭이 아니라 JS 가 일으키므로
        // 전역 리스너가 잡지 못한다.
        window.pageLoading?.start();
        window.location.href = this.pendingUrl;
      }
    }
  );
}
